<script setup lang="ts">
import { ref, reactive, inject, computed, type ComputedRef } from 'vue'
import {
  NForm,
  NFormItem,
  NInput,
  NUpload,
  type FormInst,
  type FormItemRule,
  type UploadFileInfo,
  NCheckbox,
} from 'naive-ui'
import { useI18n } from 'vue-i18n'

import { type CanvasRetrieve } from '@openapi'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { type CarouselProps } from '@canvas-builder/types/canvas'
import { simpleDeepClone } from '@canvas-builder/util/clone'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import { getFileExtension } from '@canvas-builder/util/file.util'
import { isValidUrl } from '@canvas-builder/util/url'
import useConnectUpload from '@lib/composables/useConnectUpload'
import { type PitcherInfo } from '@lib/types/app'
import { SUPPORTED_IMAGE_TYPES } from '@lib/constants/uploads.const'

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => undefined),
)

const props = withDefaults(
  defineProps<{
    id: string
    usedInSectionId: CanvasRetrieve['id']
    data: CarouselProps
  }>(),
  {
    usedInSectionId: '',
  },
)

const { t } = useI18n()
const { setComponentEditMode, updateNodeData } = useCanvas()
const formRef = ref<FormInst | null>(null)
const uploadingSlideIndices = ref(new Set<number>())
const { uploadToConnect } = useConnectUpload()

type ImagesInForm = (NonNullable<CarouselProps['images']>[number] & {
  feedback?: string
  validationStatus?: 'success' | 'error' | 'warning'
})[]

const formValues = reactive({
  images: (props.data?.images
    ? simpleDeepClone(props.data.images)
    : [{ url: '', is_uploaded_asset: false }]) as ImagesInForm,
  placeholder_height: props.data?.placeholder_height || '500px',
  include_all_images_in_pdf: props.data?.include_all_images_in_pdf ?? true,
})

function removeItem(index: number) {
  formValues.images.splice(index, 1)
}

function addItem() {
  formValues.images.push({ url: '', is_uploaded_asset: false })
}

function validateUpload(files: Array<UploadFileInfo>): Promise<UploadFileInfo['file']> {
  return new Promise((resolve, reject) => {
    if (!files?.length) {
      return reject(new Error(t('canvasUI.canvasBuilder.carousel.validation.requiredFile')))
    }
    const file = files?.[0]?.file
    if (!file) {
      return reject(new Error(t('canvasUI.canvasBuilder.carousel.validation.invalidFile')))
    }
    const extension = getFileExtension(file.name)

    if (!SUPPORTED_IMAGE_TYPES.includes(extension)) {
      return reject(new Error(t('canvasUI.canvasBuilder.carousel.validation.invalidImageExtension', { extension })))
    }

    return resolve(file)
  })
}

function urlValidator(_: FormItemRule, v: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!v) return reject(new Error(t('canvasUI.canvasBuilder.carousel.form.errors.requiredUrl')))
    if (v && !isValidUrl(v)) return reject(new Error(t('canvasUI.canvasBuilder.carousel.form.errors.invalidUrl')))
    return resolve()
  })
}

async function onUpdateFileList(files: Array<UploadFileInfo>, slideIdx: number) {
  validateUpload(files)
    .then(async (f) => {
      if (!f) return

      try {
        if (!pitcherInfo.value) throw new Error('Pitcher info is not provided - Carousel.settings.vue')

        const formData = new FormData()
        formData.append('content', f)
        formData.append('name', f.name)
        formData.append('instance_id', pitcherInfo.value.instanceId)
        formData.append('is_asset', 'true')
        formData.append('access_token', pitcherInfo.value.accessToken)
        uploadingSlideIndices.value.add(slideIdx)
        const r = await uploadToConnect(formData)

        formValues.images[slideIdx].url = r.url
        formValues.images[slideIdx].is_uploaded_asset = true

        uploadingSlideIndices.value.delete(slideIdx)
      } catch (e) {
        console.error(e)
        uploadingSlideIndices.value.delete(slideIdx)
      }
    })
    .catch((e) => {
      formValues.images[slideIdx].feedback = e.message
      formValues.images[slideIdx].validationStatus = 'error'
    })
}

function onClearUploadedAsset(idx: number) {
  formValues.images[idx].url = ''
  formValues.images[idx].is_uploaded_asset = false
}

function onSave() {
  formRef.value
    ?.validate((errors) => {
      if (!errors) {
        updateNodeData(
          {
            images: formValues.images.map(({ feedback, validationStatus, ...rest }) => rest),
            placeholder_height: formValues.placeholder_height,
            include_all_images_in_pdf: formValues.include_all_images_in_pdf,
          } as CarouselProps,
          props.id,
          props.usedInSectionId,
        )
        setComponentEditMode(false)
      }
    })
    .catch(() => {})
}

defineExpose({
  isBusy: computed(() => uploadingSlideIndices.value.size > 0),
})
</script>

<template>
  <NForm ref="formRef" class="flex flex-col h-full" :model="formValues">
    <NFormItem class="w-full" :label="t('canvasUI.canvasBuilder.carousel.form.placeholderHeight')">
      <NInput v-model:value.trim="formValues.placeholder_height" clearable placeholder="" />
    </NFormItem>

    <NFormItem class="mb-4" :label="t('canvasUI.canvasBuilder.carousel.form.rendering')" :show-feedback="false">
      <NCheckbox v-model:checked="formValues.include_all_images_in_pdf">
        {{ t('canvasUI.canvasBuilder.carousel.form.includeAllImagesInPdf') }}
      </NCheckbox>
    </NFormItem>

    <NFormItem
      v-for="(item, idx) in formValues.images"
      :key="idx"
      class="mb-3 relative"
      :feedback="item.feedback"
      :label="`${t('canvasUI.canvasBuilder.carousel.form.slide')} ${idx + 1}`"
      :path="`images[${idx}].url`"
      :rule="{
        validator: urlValidator,
        trigger: ['input', 'blur'],
      }"
      style="border-bottom: 1px solid whitesmoke"
      :validation-status="item.validationStatus"
    >
      <CImage v-if="item.is_uploaded_asset && item.url" :img-props="{ class: 'max-w-[232px]' }" :src="item.url" />
      <NInput
        v-else
        v-model:value="item.url"
        class="w-full"
        clearable
        :disabled="item.is_uploaded_asset || uploadingSlideIndices.has(idx)"
        :on-input="
          () => {
            formValues.images.splice(idx, 1, {
              ...formValues.images[idx],
              feedback: undefined,
              validationStatus: undefined,
            })
          }
        "
        :placeholder="t('canvasUI.canvasBuilder.carousel.form.urlInputPlaceholder')"
      />

      <CButton
        class="absolute w-8 h-8 top-[-34px] left-[50px]"
        :disabled="uploadingSlideIndices.size > 0"
        size="small"
        text
        type="error"
        @click="removeItem(idx)"
      >
        <CIcon fa-type="fas" icon="trash" size="12px" />
      </CButton>
      <NUpload
        v-if="!item.is_uploaded_asset"
        class="w-13 h-9 ml-2"
        :default-upload="false"
        :disabled="uploadingSlideIndices.has(idx)"
        :file-list="[]"
        :max="1"
        :multiple="false"
        :on-update:file-list="(f) => onUpdateFileList(f, idx)"
        :show-file-list="false"
      >
        <CButton :loading="uploadingSlideIndices.has(idx)" secondary>
          <CIcon v-if="!uploadingSlideIndices.has(idx)" icon="upload" size="16px" />
        </CButton>
      </NUpload>
      <CButton v-else class="w-13 h-9 ml-2" text @click="onClearUploadedAsset(idx)">
        <CIcon icon="circle-xmark" size="16px" />
      </CButton>
    </NFormItem>

    <CButton block class="mt-auto" @click="addItem"> {{ t('canvasUI.canvasBuilder.carousel.form.newSlide') }} </CButton>
    <CButton block class="mt-3" :disabled="uploadingSlideIndices.size > 0" type="primary" @click="onSave">
      {{ t('canvasUI.common.done') }}
    </CButton>
  </NForm>
</template>
